import { useAuthContext } from "providers/Auth";
import { FC } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  ContextMenuPopover,
  type Action
} from "shared/components/ContextMenuPopover";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Plus } from "untitledui-js-base";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  PlazaIcon,
  File02Icon
} from "@hugeicons-pro/core-stroke-rounded";

type Props = {
  handleCopyLink: (link: string) => void;
  setShowAddUnitModal?: (show: boolean) => void;
};

export const LayoutButtons: FC<Props> = ({
  handleCopyLink,
  setShowAddUnitModal
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type, partner } = useAuthContext();
  const { adminPrivileges } = type;

  if (!type.isSDK && type.isBoomScreen) {
    const partnerListingUrl = partner?.listing_url ?? "";
    const partnerApplicationLink = partner?.magic_link?.application_link ?? "";

    const boomCRMActions: Action[] = [
      {
        title: (
          <div className="flex items-center gap-3">
            <HugeiconsIcon
              size={20}
              icon={PlazaIcon}
              className="text-content-200"
            />
            <p className="text-base font-medium text-content-1000">
              Copy listings link
            </p>
          </div>
        ),
        disabled: !partnerListingUrl,
        tooltip: !partnerListingUrl ? "No listings link available" : undefined,
        onClick: () => handleCopyLink(partnerListingUrl)
      },
      {
        title: (
          <div className="flex items-center gap-3">
            <HugeiconsIcon
              size={20}
              icon={File02Icon}
              className="text-content-200"
            />
            <p className="text-base font-medium text-content-1000">
              Copy portfolio link
            </p>
          </div>
        ),
        disabled: !partnerApplicationLink,
        tooltip: !partnerApplicationLink
          ? "No portfolio link available"
          : undefined,
        onClick: () => handleCopyLink(partnerApplicationLink)
      }
    ];

    return (
      <>
        {type.isBoomCRM ? (
          <ContextMenuPopover
            placement="bottom-end"
            cbTrigger={(open) => (
              <Button
                variant={ButtonVariant.BASE}
                className="w-full md:w-auto"
                size={ButtonSize.SM}
                HugeIcon={open ? ArrowUp01Icon : ArrowDown01Icon}
                iconPosition="right"
              >
                Copy link
              </Button>
            )}
            actions={boomCRMActions}
          />
        ) : (
          <Button
            variant={ButtonVariant.BASE}
            className="w-full md:w-auto"
            size={ButtonSize.SM}
            onClick={() => handleCopyLink(partnerApplicationLink)}
          >
            Copy portfolio link
          </Button>
        )}
        {adminPrivileges && setShowAddUnitModal ? (
          <Button
            className="w-full md:w-auto"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={() => setShowAddUnitModal(true)}
            Icon={Plus}
          >
            Add {dictionary.Unit}
          </Button>
        ) : null}
      </>
    );
  }

  if (
    (type.isManual || type.canManageEntitiesDirectly) &&
    adminPrivileges &&
    setShowAddUnitModal
  )
    return (
      <Button
        className="w-full md:w-auto"
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={() => setShowAddUnitModal(true)}
        Icon={Plus}
      >
        Add {dictionary.Unit}
      </Button>
    );

  return null;
};
